/**
 * OverMaxMethodologyFooter — methodology disclosure for the over-max KPI.
 *
 * §5.BU §I.3 P3 Commit B (2026-06-19). Locked in
 *   docs/audits/2026-06-19_5bu_i3_p2_design.md §C.3 (LOAD-BEARING L2)
 *
 * Renders a small (12px gray) footer block BELOW the KpiCardGrid (NOT
 * inside any card) that surfaces the over-max methodology decision
 * explicitly:
 *
 *   "Over-Max calculation: dollars where on-hand exceeds max threshold.
 *    1,710 SKUs (29.7%) excluded because no maximum is defined
 *    (max_qty=0). Naive calc including these SKUs would report
 *    $1,011,480; XMEK's pre-computed unallocated-over-max reports
 *    $682,497."
 *
 * Verbatim citation of both alternate methodologies' actual headline
 * figures per Consult #2 L2 amendment (closes the Rule 116 number-
 * citation drift risk in the original PROPOSED $293K phrasing).
 *
 * Renders nothing when over_max_position data is absent (e.g., supplier
 * has all max_qty=0 SKUs or no inventory at all).
 */
import React from 'react';

function formatCurrency(v) {
  if (v === null || v === undefined) return 'Not available';
  return `$${Number(v).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
}

function OverMaxMethodologyFooter({ overMaxPosition }) {
  const om = overMaxPosition || {};
  const zero = om.skus_max_zero_count;
  const total = om.skus_total_count;
  const naive = om.over_max_dollar_naive;
  const xmek = om.over_max_dollar_xmek_unallocated;

  // Only render when we have the data to surface the methodology.
  // Both alternate-methodology figures must be present to make the
  // sub-text contract-compliant.
  if (
    zero === null || zero === undefined
    || !total
    || naive === null || naive === undefined
    || xmek === null || xmek === undefined
  ) {
    return null;
  }

  const pct = total > 0 ? (zero / total) * 100 : 0;

  return (
    <div
      data-testid="over-max-methodology-footer"
      style={{
        marginTop: '4px',
        marginBottom: '24px',
        padding: '10px 12px',
        backgroundColor: 'var(--panel-2)',
        borderRadius: '6px',
        fontSize: '12px',
        color: 'var(--text-3)',
        lineHeight: 1.5,
      }}
    >
      <span style={{ fontWeight: 'var(--font-weight-medium)', color: 'var(--text)' }}>
        Over-Max methodology:{' '}
      </span>
      dollars where on-hand exceeds max threshold.{' '}
      <strong>{zero.toLocaleString()} SKUs ({pct.toFixed(1)}%)</strong>{' '}
      excluded because no maximum is defined (max_qty=0). Naive calc
      including these SKUs would report{' '}
      <strong>{formatCurrency(naive)}</strong>; XMEK's pre-computed
      unallocated-over-max reports{' '}
      <strong>{formatCurrency(xmek)}</strong>.
    </div>
  );
}

export default OverMaxMethodologyFooter;
